import { LitElement, html, css, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export interface AddColumnRequest {
  periodType: 'duration' | 'instant' | 'mixed';
  startDate?: string;
  endDate?: string;
  instantDate?: string;
  selectedDimensions?: DimensionSelection[];
}

export interface DimensionSelection {
  axisId: string;
  axisLabel: string;
  memberId?: string;
  memberLabel?: string;
  isTyped?: boolean;
  typedValue?: string;
}

export interface AvailableDimension {
  id: string;
  conceptName: string;
  axisLabel: string;
  members?: AvailableDimensionMember[];
  typedMember?: {
    id: string;
    dataType: string;
  };
}

export interface AvailableDimensionMember {
  id: string;
  label: string;
}

@customElement('jupiter-add-column-dialog')
export class JupiterAddColumnDialog extends LitElement {
  @property({ type: String }) periodType: 'duration' | 'instant' | 'mixed' = 'duration';
  @property({ type: Boolean }) open = false;
  @property({ type: Array }) availableDimensions: AvailableDimension[] = [];

  @state() private _startDate = '';
  @state() private _endDate = '';
  @state() private _instantDate = '';
  @state() private _selectedType: 'duration' | 'instant' = 'duration';
  @state() private _selectedDimensions: Map<string, DimensionSelection> = new Map();

  updated(changedProperties: PropertyValues) {
    if (changedProperties.has('open') && this.open) {
      // Reset form when dialog opens
      this._resetForm();
    }
    
    if (changedProperties.has('availableDimensions')) {
      //////console.log(`🎯 Add Column Dialog received ${this.availableDimensions.length} dimensions:`, this.availableDimensions.map(d => d.axisLabel));
    }
  }

  static styles = css`
    :host {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.3s ease, visibility 0.3s ease;
    }

    :host([open]) {
      opacity: 1;
      visibility: visible;
    }

    .dialog {
      background: white;
      border-radius: 8px;
      padding: 24px;
      min-width: 400px;
      max-width: 500px;
      max-height: 90vh;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
      transform: scale(0.9);
      transition: transform 0.3s ease;
      display: flex;
      flex-direction: column;
    }

    :host([open]) .dialog {
      transform: scale(1);
    }

    .dialog-header {
      margin-bottom: 20px;
      flex-shrink: 0;
    }

    .dialog-title {
      font-size: 20px;
      font-weight: 600;
      color: var(--jupiter-text-primary, #333);
      margin: 0 0 8px 0;
    }

    .dialog-description {
      font-size: 14px;
      color: var(--jupiter-text-secondary, #666);
      margin: 0;
    }

    .dialog-content {
      margin-bottom: 24px;
      flex: 1;
      overflow-y: auto;
      max-height: calc(90vh - 160px);
    }

    .form-group {
      margin-bottom: 16px;
    }

    .form-group.date-row {
      display: flex;
      gap: 12px;
    }

    .form-group.date-row .date-field {
      flex: 1;
    }

    .form-label {
      display: block;
      font-weight: 500;
      margin-bottom: 6px;
      color: var(--jupiter-text-primary, #333);
      font-size: 14px;
    }

    .form-label.required::after {
      content: ' *';
      color: var(--jupiter-error-color, #d32f2f);
    }

    .form-input {
      width: 100%;
      box-sizing: border-box;
      padding: 10px 12px;
      border: 1px solid var(--jupiter-border-color, #ddd);
      border-radius: 4px;
      font-size: 14px;
      font-family: inherit;
      transition: border-color 0.2s ease;
    }

    .form-input:focus {
      outline: none;
      border-color: var(--jupiter-primary-color, #007bff);
      box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    }

    select.form-input {
      cursor: pointer;
      background-color: white;
    }

    .dialog-actions {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      flex-shrink: 0;
      border-top: 1px solid var(--jupiter-border-color, #ddd);
      padding-top: 16px;
      margin-top: 16px;
    }

    .btn {
      padding: 10px 20px;
      border: none;
      border-radius: 4px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.2s ease;
    }

    .btn-cancel {
      background: var(--jupiter-neutral-background, #f5f5f5);
      color: var(--jupiter-text-primary, #333);
    }

    .btn-cancel:hover {
      background: var(--jupiter-neutral-background-hover, #e0e0e0);
    }

    .btn-primary {
      background: var(--jupiter-primary-color, #667eea);
      color: white;
    }

    .btn-primary:hover {
      background: var(--jupiter-primary-color-dark, #5a6fd8);
    }

    .btn-primary:disabled {
      background: var(--jupiter-disabled-background, #ccc);
      cursor: not-allowed;
    }

    /* Dimension Selection Styles */
    .dimensions-section {
      margin-top: 20px;
      padding-top: 20px;
      border-top: 1px solid var(--jupiter-border-color, #ddd);
    }

    .dimensions-section h3 {
      margin: 0 0 16px 0;
      font-size: 16px;
      font-weight: 500;
      color: var(--jupiter-text-primary, #333);
    }

    .dimension-item {
      margin-bottom: 16px;
      padding: 16px;
      border: 1px solid var(--jupiter-border-color, #ddd);
      border-radius: 4px;
      background: var(--jupiter-card-background, #fafafa);
    }

    .dimension-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;
    }

    .dimension-checkbox {
      margin: 0;
    }

    .dimension-label {
      font-weight: 500;
      color: var(--jupiter-text-primary, #333);
      flex: 1;
    }

    .dimension-details {
      margin-left: 24px;
    }

    .member-selection {
      margin-top: 12px;
    }

    .member-selection label {
      display: block;
      margin-bottom: 4px;
      font-size: 13px;
      color: var(--jupiter-text-secondary, #666);
    }

    .typed-input {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid var(--jupiter-border-color, #ddd);
      border-radius: 4px;
      font-size: 14px;
    }

    .member-list {
      max-height: 120px;
      overflow-y: auto;
      border: 1px solid var(--jupiter-border-color, #ddd);
      border-radius: 4px;
      padding: 8px;
    }

    .member-option {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px 0;
    }

    .member-option input[type="radio"] {
      margin: 0;
    }

    .member-option label {
      margin: 0;
      font-size: 13px;
      color: var(--jupiter-text-primary, #333);
      cursor: pointer;
    }

    .no-dimensions {
      text-align: center;
      color: var(--jupiter-text-secondary, #666);
      font-style: italic;
      padding: 20px;
    }

    .form-description {
      font-size: 13px;
      color: var(--jupiter-text-secondary, #666);
      margin: 0 0 12px 0;
      line-height: 1.4;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._resetForm();
  }

  willUpdate(changedProperties: Map<string, unknown>) {
    super.willUpdate(changedProperties);
    if (changedProperties.has('open') && this.open) {
      this._resetForm();
    }
  }

  private _handleCancel() {
    this.open = false;
    this.dispatchEvent(new CustomEvent('dialog-cancel', { bubbles: true }));
  }

  private _handleConfirm() {
    if (!this._isFormValid()) return;

    const request: AddColumnRequest = {
      periodType: this.periodType === 'mixed' ? this._selectedType : this.periodType
    };

    if (this.periodType === 'instant' || (this.periodType === 'mixed' && this._selectedType === 'instant')) {
      request.instantDate = this._instantDate;
    } else {
      request.startDate = this._startDate;
      request.endDate = this._endDate;
    }

    // Add selected dimensions
    if (this._selectedDimensions.size > 0) {
      request.selectedDimensions = Array.from(this._selectedDimensions.values());
    }

    this.dispatchEvent(new CustomEvent('column-add', {
      detail: request,
      bubbles: true
    }));
  }

  private _isFormValid(): boolean {
    if (this.periodType === 'mixed') {
      if (this._selectedType === 'instant') {
        return !!this._instantDate;
      } else {
        return !!this._startDate && !!this._endDate && this._startDate <= this._endDate;
      }
    } else if (this.periodType === 'duration') {
      return !!this._startDate && !!this._endDate && this._startDate <= this._endDate;
    } else if (this.periodType === 'instant') {
      return !!this._instantDate;
    }
    return false;
  }

  private _handleStartDateChange(e: Event) {
    this._startDate = (e.target as HTMLInputElement).value;
  }

  private _handleEndDateChange(e: Event) {
    this._endDate = (e.target as HTMLInputElement).value;
  }

  private _handleSelectedTypeChange(e: Event) {
    this._selectedType = (e.target as HTMLSelectElement).value as 'duration' | 'instant';
  }

  private _handleInstantDateChange(e: Event) {
    this._instantDate = (e.target as HTMLInputElement).value;
  }

  private _handleDimensionToggle(dimensionId: string, event: Event) {
    const checkbox = event.target as HTMLInputElement;
    
    if (checkbox.checked) {
      const dimension = this.availableDimensions.find(d => d.id === dimensionId);
      if (dimension) {
        const selection: DimensionSelection = {
          axisId: dimension.id,
          axisLabel: dimension.axisLabel,
          isTyped: !!(dimension.typedMember && (!dimension.members || dimension.members.length === 0))
        };
        
        this._selectedDimensions.set(dimensionId, selection);
      }
    } else {
      this._selectedDimensions.delete(dimensionId);
    }
    
    this.requestUpdate();
  }

  private _handleMemberSelection(dimensionId: string, memberId: string, memberLabel: string) {
    const existing = this._selectedDimensions.get(dimensionId);
    if (existing) {
      existing.memberId = memberId;
      existing.memberLabel = memberLabel;
      this._selectedDimensions.set(dimensionId, existing);
      this.requestUpdate();
    }
  }

  private _handleTypedValueChange(dimensionId: string, event: Event) {
    const input = event.target as HTMLInputElement;
    const existing = this._selectedDimensions.get(dimensionId);
    if (existing) {
      existing.typedValue = input.value;
      this._selectedDimensions.set(dimensionId, existing);
    }
  }

  private _resetForm() {
    const today = new Date().toISOString().split('T')[0];
    this._startDate = today;
    this._endDate = today;
    this._instantDate = today;
    this._selectedType = this.periodType === 'instant' ? 'instant' : 'duration';
    this._selectedDimensions.clear();
    
    // Auto-select dimensions with only one member
    this._autoSelectSingleMemberDimensions();
    
    this.requestUpdate();
  }

  private _autoSelectSingleMemberDimensions() {
    this.availableDimensions.forEach(dimension => {
      // Auto-select if dimension has exactly one member
      if (dimension.members && dimension.members.length === 1) {
        const member = dimension.members[0];
        const selection: DimensionSelection = {
          axisId: dimension.id,
          axisLabel: dimension.axisLabel,
          memberId: member.id,
          memberLabel: member.label,
          isTyped: false
        };
        this._selectedDimensions.set(dimension.id, selection);
        ////console.log(`🎯 Auto-selected single member: ${dimension.axisLabel} -> ${member.label}`);
      }
      // Auto-select typed dimensions (no domain members, only typed input)
      else if (dimension.typedMember && (!dimension.members || dimension.members.length === 0)) {
        const selection: DimensionSelection = {
          axisId: dimension.id,
          axisLabel: dimension.axisLabel,
          isTyped: true
          // Note: typedValue will be entered in column header, not here
        };
        this._selectedDimensions.set(dimension.id, selection);
        ////console.log(`🎯 Auto-selected typed dimension: ${dimension.axisLabel} (value will be entered in column header)`);
      }
    });
  }

  render() {
    if (!this.open) return html``;

    const isValid = this._isFormValid();

    return html`
      <div class="dialog" @click="${(e: Event) => e.stopPropagation()}">
        <div class="dialog-header">
          <h2 class="dialog-title">Add Column</h2>

        </div>

        <div class="dialog-content">
          ${this.periodType === 'mixed' ? html`
            <div class="form-group">
              <label class="form-label required">Column Type</label>
              <select 
                class="form-input"
                .value="${this._selectedType}"
                @change="${this._handleSelectedTypeChange}"
                required
              >
                <option value="instant">Instant (single date)</option>
                <option value="duration">Duration (start and end dates)</option>
              </select>
            </div>
          ` : ''}

          ${(this.periodType === 'instant' || (this.periodType === 'mixed' && this._selectedType === 'instant')) ? html`
            <div class="form-group">
              <label class="form-label required">Instant Date</label>
              <input 
                type="date" 
                class="form-input"
                .value="${this._instantDate}"
                @change="${this._handleInstantDateChange}"
                required
              />
            </div>
          ` : html`
            <div class="form-group date-row">
              <div class="date-field">
                <label class="form-label required">Start Period Date</label>
                <input 
                  type="date" 
                  class="form-input"
                  .value="${this._startDate}"
                  @change="${this._handleStartDateChange}"
                  required
                />
              </div>
              <div class="date-field">
                <label class="form-label required">End Period Date</label>
                <input 
                  type="date" 
                  class="form-input"
                  .value="${this._endDate}"
                  @change="${this._handleEndDateChange}"
                  required
                />
              </div>
            </div>
          `}

          <!-- Dimensions Section -->
          ${this.availableDimensions.length > 0 ? html`
            <div class="dimensions-section">
              <h3>Available Dimensions</h3>
              <p class="form-description">Select dimensions to include in the column. Domain members can be selected here, while typed dimension values will be entered directly in the column header.</p>
              
              ${this.availableDimensions.map(dimension => {
                const isSelected = this._selectedDimensions.has(dimension.id);
                const selection = this._selectedDimensions.get(dimension.id);
                const isTypedDimension = !!(dimension.typedMember && (!dimension.members || dimension.members.length === 0));
                
                return html`
                  <div class="dimension-item">
                    <div class="dimension-header">
                      <input 
                        type="checkbox" 
                        class="dimension-checkbox"
                        .checked="${isSelected}"
                        @change="${(e: Event) => this._handleDimensionToggle(dimension.id, e)}"
                        id="dim-${dimension.id}"
                      />
                      <label for="dim-${dimension.id}" class="dimension-label">
                        ${dimension.axisLabel || dimension.conceptName}
                      </label>
                    </div>
                    
                    ${isSelected ? html`
                      <div class="dimension-details">
                        ${isTypedDimension ? html`
                          <!-- Typed Dimension Input -->
                          <div class="member-selection">
                            <label>Enter value for ${dimension.conceptName}:</label>
                            <input
                              type="text"
                              class="typed-input"
                              .value="${selection?.typedValue || ''}"
                              @input="${(e: Event) => this._handleTypedValueChange(dimension.id, e)}"
                              placeholder="Enter value..."
                            />
                          </div>
                        ` : html`
                          <!-- Domain Members Selection -->
                          ${dimension.members && dimension.members.length > 0 ? html`
                            <div class="member-selection">
                              <label>Select member:</label>
                              <div class="member-list">
                                ${dimension.members.map(member => html`
                                  <div class="member-option">
                                    <input
                                      type="radio"
                                      name="member-${dimension.id}"
                                      .checked="${selection?.memberId === member.id}"
                                      @change="${() => this._handleMemberSelection(dimension.id, member.id, member.label)}"
                                      id="member-${dimension.id}-${member.id}"
                                    />
                                    <label for="member-${dimension.id}-${member.id}">
                                      ${member.label}
                                    </label>
                                  </div>
                                `)}
                              </div>
                            </div>
                          ` : html`
                            <p class="form-description">No members available for this dimension</p>
                          `}
                        `}
                      </div>
                    ` : ''}
                  </div>
                `;
              })}
            </div>
          ` : ''}
        </div>

        <div class="dialog-actions">
          <button class="btn btn-cancel" @click="${this._handleCancel}">
            Cancel
          </button>
          <button 
            class="btn btn-primary" 
            ?disabled="${!isValid}"
            @click="${this._handleConfirm}"
          >
            Add Column
          </button>
        </div>
      </div>
    `;
  }
}
